import { Link } from 'react-router';

import { Logo } from '@/components/brand/Logo';
import { LogoMark } from '@/components/brand/LogoMark';
import { PageContainer } from '@/components/landing/sections';
import { useSignupOpen } from '@/lib/signup';

/**
 * 公開ページ共通のフッター。
 *
 * LP と法務ページ (利用規約・プライバシーポリシー・特商法表記) で同じものを出す。
 * 法務リンクは 1 箇所にしか無い状態を保ちたいので、ここを唯一の置き場にする。
 *
 * LP 内の節へのリンクは `/#...` の素のアンカーにしてある。LP に居るときは
 * 同一ドキュメント内の移動になり、法務ページからは LP へ遷移してから
 * その位置に飛ぶ。React Router の Link ではハッシュまでスクロールしない。
 */

function FooterHeading({ children }: { children: string }) {
  return <span className="font-mono text-xs tracking-[0.14em] text-ink-subtle">{children}</span>;
}

const linkClass = 'text-sm text-ink-muted transition-colors hover:text-ink';

/*
  名前の由来。JSX に直に書くと、ソースの改行位置がそのまま半角スペースになって
  「日本地図を 遺した」のように語の途中が空く。文字列として繋いで渡す。
*/
const NAME_ORIGIN = [
  'INOU という名前は、日本中を自らの足で歩いて測量し、',
  '初めて実測に基づく日本地図を遺した伊能忠敬に由来します。',
  '日々の歩み——つまり工数——を正確に測り続ければ、やがて全体の輪郭が見えてくる。',
  '同時に「I know」、すなわち自分たちのプロジェクトの状況を把握している状態を指しています。',
].join('');

const LOGO_ORIGIN = [
  'ロゴのマークもその含意を写しています。',
  '開いた円は測量の基準円、中心の点は測量の基点、そこから伸びる直線は基点から引いた測線、',
  '下へ抜ける縦線は現地に立てる標尺です。',
  '記録が一点ずつ積み上がって地図になる、という順序をそのまま形にしています。',
].join('');

/**
 * @param signedIn ログイン済みなら「はじめる」列を中への導線に差し替える。
 *   法務ページはログイン状態を持たないので既定は未ログイン扱い。
 */
export function SiteFooter({ signedIn = false }: { signedIn?: boolean }) {
  /*
   * サインアップの開閉は prop で受けない。ここは法務ページ (`LegalLayout`) からも
   * 出るが、あちらはログイン状態すら持っていないので、渡す側が判断できない。
   */
  const signupOpen = useSignupOpen();

  return (
    <footer className="border-t border-line bg-surface-raised">
      <PageContainer className="pt-10 pb-8 sm:pt-12">
        {/* 狭い幅では 4 列が縦一列になる。列の間隔をそのまま積むと間延びする */}
        <div className="grid gap-7 pb-8 sm:grid-cols-2 sm:gap-10 sm:pb-10 lg:grid-cols-4">
          <div className="flex flex-col gap-3">
            <Logo height={36} className="text-brand-500" />
            {/*
              読点で折らずに 2 行に固定する。ブラウザ任せの折り返しだと
              列幅によって「予実管理 SaaS。」の途中で切れる幅が出るため。
            */}
            <p className="text-sm leading-relaxed text-ink-subtle">
              工数入力の摩擦をゼロにする
              <br />
              予実管理 SaaS。
            </p>
          </div>

          <nav className="flex flex-col gap-3">
            <FooterHeading>PRODUCT</FooterHeading>
            <a href="/#how-it-works" className={linkClass}>
              仕組み
            </a>
            <a href="/#reports" className={linkClass}>
              集計できること
            </a>
            <a href="/#pricing" className={linkClass}>
              料金プラン
            </a>
            {/*
              ヘルプは列を増やさずここに入れる。列を足すと lg:grid-cols-4 が
              崩れて、フッター全体の並びを組み直すことになる。
            */}
            <Link to="/help" className={linkClass}>
              ヘルプ
            </Link>
          </nav>

          <nav className="flex flex-col gap-3">
            <FooterHeading>LEGAL</FooterHeading>
            <Link to="/terms" className={linkClass}>
              利用規約
            </Link>
            <Link to="/privacy" className={linkClass}>
              プライバシーポリシー
            </Link>
            <Link to="/legal/commerce" className={linkClass}>
              特定商取引法に基づく表記
            </Link>
          </nav>

          <nav className="flex flex-col gap-3">
            <FooterHeading>GET STARTED</FooterHeading>
            {signedIn ? (
              <Link to="/utilization" className={linkClass}>
                ダッシュボードへ
              </Link>
            ) : (
              <>
                {/* 閉じている間は出さない。押しても着地できないリンクを残さない */}
                {signupOpen ? (
                  <Link to="/signup" className={linkClass}>
                    14日間無料で試す
                  </Link>
                ) : null}
                <Link to="/login" className={linkClass}>
                  ログイン
                </Link>
              </>
            )}
          </nav>
        </div>

        {/*
          名前の由来。主張はさせたくないので、罫線の内側に小さく置く。
          出典は README の冒頭。
        */}
        <div className="relative overflow-hidden border-t border-line py-6 sm:py-8">
          {/*
            由来の文章の背景に、ロゴのマーク (測量の基準円と標尺) を斜めに敷く。

            断ち切らずに全体を入れる。標尺が地面に刺さっている見え方にしたいので、
            少しだけ傾ける (14deg)。傾けた分の高さが要るため、マークは
            ブロックの内寸に収まる 200px に留めてある。

            本文と重なると読みにくくなるので、見出し 9rem + 本文 34rem を置いても
            右が余る lg 以上でだけ出す。文字は下の relative なコンテナで上に載る。
          */}
          <LogoMark
            height={200}
            className="pointer-events-none absolute top-1/2 right-10 hidden -translate-y-1/2 rotate-[14deg] text-brand-500 opacity-[0.12] lg:block"
          />
          <div className="relative grid gap-x-12 gap-y-4 sm:grid-cols-[9rem_minmax(0,34rem)]">
            <FooterHeading>ABOUT THE NAME</FooterHeading>
            {/*
              由来は 2 段落で、見出しの隣に落とす。1 本の <p> を <br> で割ると
              段落の切れ目が行間に埋もれ、行長も罫線いっぱいまで伸びて追いにくい。
              本文色は ink-faint (2.3:1) では読めないので ink-muted まで上げる。
            */}
            <div className="flex flex-col gap-3 text-sm leading-relaxed text-ink-muted">
              <p>{NAME_ORIGIN}</p>
              <p>{LOGO_ORIGIN}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
          <span className="font-mono text-xs text-ink-faint">
            © {new Date().getFullYear()} INOU
          </span>
          <span className="text-xs text-ink-faint">Cloudflare Workers 上で動いています</span>
        </div>
      </PageContainer>
    </footer>
  );
}
